import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeft, Mail, Lock, User, Loader2, Copy, Check, MessageSquare,
  Send, Share2, PartyPopper, AlertCircle,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Logo } from '@/components/Logo';
import { supabase } from '@/integrations/supabase/client';
import { setStoredViewMode } from '@/hooks/useViewMode';
import { PasswordStrengthMeter, usePasswordStrength } from '@/components/PasswordStrengthMeter';
import { toast } from 'sonner';

const SPORTS = [
  { id: 'soccer', label: 'Soccer', emoji: '⚽' },
  { id: 'football', label: 'Football', emoji: '🏈' },
  { id: 'basketball', label: 'Basketball', emoji: '🏀' },
  { id: 'baseball', label: 'Baseball', emoji: '⚾' },
];


const TOTAL_STEPS = 4;
export const COACH_SIGNUP_ACTIVE_KEY = 'sportara-coach-signup-active';
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;


interface CoachSignupFlowProps {
  /** Back out of the coach flow to the homepage. */
  onExit: () => void;
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return (
    <p className="flex items-center gap-1.5 text-xs text-destructive font-body pl-1">
      <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {message}
    </p>
  );
}

function StepHeader({ step, onBack, title, subtitle }: {
  step: number; onBack?: () => void; title: string; subtitle?: string;
}) {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        {onBack ? (
          <button
            onClick={onBack}
            className="w-9 h-9 rounded-xl border border-border/60 bg-card/60 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Go back"
          >
            <ArrowLeft className="w-4 h-4" />
          </button>
        ) : (
          <div className="w-9 h-9 rounded-xl bg-coach/10 flex items-center justify-center">
            <Check className="w-4 h-4 text-coach" />
          </div>
        )}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-[11px] font-display font-bold uppercase tracking-wider text-coach">
              Step {step} of {TOTAL_STEPS}
            </span>
            <span className="text-[11px] font-body text-muted-foreground">Four short steps</span>
          </div>
          <div className="h-1.5 rounded-full bg-muted overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-coach"
              initial={false}
              animate={{ width: `${(step / TOTAL_STEPS) * 100}%` }}
              transition={{ type: 'spring', stiffness: 160, damping: 22 }}
            />
          </div>
        </div>
      </div>
      {step < 4 && <p className="text-xs text-muted-foreground font-body">Next: {['set up your team', 'invite players', 'assign your first lesson'][step - 1]}</p>}
      {title && (
        <div>
          <h2 className="font-display font-bold text-2xl tracking-tight">{title}</h2>
          {subtitle && <p className="text-sm text-muted-foreground font-body mt-1">{subtitle}</p>}
        </div>
      )}
    </div>
  );
}

export function CoachSignupFlow({ onExit }: CoachSignupFlowProps) {
  const [step, setStep] = useState(1);
  const [accountCreated, setAccountCreated] = useState(false);
  const [resuming, setResuming] = useState(true);
  useEffect(() => {
    const onPop = () => {
      if (window.location.pathname === '/auth/coach') {
        const previous = Number(new URLSearchParams(window.location.search).get('step'));
        setStep(previous >= 1 && previous <= 4 ? previous : 1);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);
  useEffect(() => {
    if (window.location.pathname !== '/auth/coach' || resuming) return;
    const current = Number(new URLSearchParams(window.location.search).get('step')) || 1;
    if (current !== step) {
      const method = step === 1 ? 'replaceState' : 'pushState';
      window.history[method]({}, '', `/auth/coach?step=${step}`);
    }
  }, [step, resuming]);

  const [loading, setLoading] = useState(false);
  const [needsEmailConfirm, setNeedsEmailConfirm] = useState(false);

  // Screen 1
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [nameError, setNameError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const passwordStrength = usePasswordStrength(password);

  // Team setup — only the sport and team name are required.
  const [sport, setSport] = useState('soccer');
  const [teamName, setTeamName] = useState('');
  const [teamError, setTeamError] = useState('');
  const [draftReady, setDraftReady] = useState(false);
  useEffect(() => { const cached = sessionStorage.getItem('sportara-coach-draft'); if (cached) { try { const d = JSON.parse(cached); setFullName(d.fullName || ''); setEmail(d.email || ''); setSport(d.sport || 'soccer'); setTeamName(d.teamName || ''); } catch { /* ignore damaged draft */ } } setDraftReady(true); }, []);
  useEffect(() => { if (draftReady) sessionStorage.setItem('sportara-coach-draft', JSON.stringify({ fullName, email, sport, teamName })); }, [draftReady, fullName, email, sport, teamName]);

  // Screen 4 — populated ONLY after a confirmed teams row insert
  const [joinCode, setJoinCode] = useState('');
  const [createdTeamName, setCreatedTeamName] = useState('');
  const [copied, setCopied] = useState(false);

  const shareMessage = joinCode
    ? `Join our team: ${window.location.origin}/join/${encodeURIComponent(joinCode)}`
    : '';

  /* ── Resume: figure out how far a returning coach already got ── */
  useEffect(() => {
    let cancelled = false;
    sessionStorage.setItem(COACH_SIGNUP_ACTIVE_KEY, '1');
    (async () => {

      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user || cancelled) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('display_name, preferred_sport, primary_role')
          .eq('user_id', user.id)
          .maybeSingle();
        if (profile?.primary_role !== 'coach') return;

        setFullName(prev => prev || profile?.display_name || '');
        setEmail(prev => prev || user.email || '');

        const { data: coachProfile } = await supabase
          .from('coach_profiles').select('id, primary_sport, onboarding_completed_at').eq('user_id', user.id).maybeSingle();
        setAccountCreated(true);
        if ((coachProfile as any)?.onboarding_completed_at && !cancelled) {
          sessionStorage.removeItem(COACH_SIGNUP_ACTIVE_KEY);
          sessionStorage.removeItem('sportara-coach-draft');
          setStoredViewMode('coach');
          window.location.replace('/coaches/dashboard');
          return;
        }
        if (!coachProfile || cancelled) { setStep(2); return; }
        setSport(coachProfile.primary_sport || 'soccer');
        const { data: team } = await supabase.from('teams').select('id, name')
          .eq('coach_id', coachProfile.id).order('created_at', { ascending: false }).limit(1).maybeSingle();
        if (team && !cancelled) {
          const { data: code } = await supabase.rpc('get_team_join_code' as any, { p_team_id: team.id });
          if (code && !cancelled) {
            setTeamName(team.name); setCreatedTeamName(team.name);
            setJoinCode(code as string); setStep(3); return;
          }
        }
        if (!cancelled) setStep(2);
      } finally {
        if (!cancelled) setResuming(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Screen 1: create the account (auth.users) ── */
  const step1Valid =
    fullName.trim().length > 1 &&
    EMAIL_RE.test(email.trim()) &&
    password.length >= 8 &&
    passwordStrength.score >= 3 &&
    !passwordStrength.isCommon;

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setNameError(''); setEmailError(''); setPasswordError('');
    let bad = false;
    if (fullName.trim().length < 2) { setNameError('Enter your full name'); bad = true; }
    if (!EMAIL_RE.test(email.trim())) { setEmailError('Enter a valid email address'); bad = true; }
    if (password.length < 8) { setPasswordError('Password must be at least 8 characters'); bad = true; }
    else if (passwordStrength.isCommon) { setPasswordError('This password appears in known data breaches — choose another'); bad = true; }
    else if (passwordStrength.score < 3) { setPasswordError('Choose a stronger password (mix letters, numbers and symbols)'); bad = true; }
    if (bad) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email: email.trim(),
        password,
        options: {
          data: {
            full_name: fullName.trim(),
            user_persona: 'coach',
            primary_role: 'coach',
            primary_sport: sport,
            referral_code: (localStorage.getItem('sportara-coach-ref') || '').replace(/[^a-z0-9]/gi, '').slice(0, 32) || undefined,
          },
          emailRedirectTo: window.location.origin,
        },
      });
      if (error) throw error;
      setStoredViewMode('coach');
      if (!data.session) { setNeedsEmailConfirm(true); return; }
      setAccountCreated(true);
      setStep(2);
    } catch (err: any) {
      setEmailError(err?.message || 'Could not create your account');
    } finally {
      setLoading(false);
    }
  };

  const waitForCoachProfile = async (userId: string) => {
    for (let i = 0; i < 5; i++) {
      const { data } = await supabase
        .from('coach_profiles')
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();
      if (data) return data.id as string;
      await new Promise(r => setTimeout(r, 600));
    }
    return null;
  };

  /* ── Screen 2: create the team (teams row → server-side join_code) ── */
  const step3Valid = teamName.trim().length > 1;

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    setTeamError('');
    if (!step3Valid) { setTeamError('Enter a team name (at least 2 characters)'); return; }
    if (joinCode) { setStep(3); return; }
    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Confirm your email from the link we sent, then create your team.');

      const coachProfileId = await waitForCoachProfile(user.id);
      if (!coachProfileId) throw new Error('Your coach profile is still being set up — try again in a moment.');

      const { error: profileError } = await supabase.from('coach_profiles').update({ primary_sport: sport }).eq('id', coachProfileId);
      if (profileError) throw profileError;
      const { error: sportError } = await supabase.from('profiles').update({ preferred_sport: sport, onboarding_completed: true }).eq('user_id', user.id);
      if (sportError) throw sportError;
      const { data: existingTeam, error: lookupError } = await supabase.from('teams').select('id').eq('coach_id', coachProfileId).ilike('name', teamName.trim()).maybeSingle();
      if (lookupError) throw lookupError;
      if (existingTeam) throw new Error('You already have a team with this name. Choose another name.');

      const { data: team, error } = await supabase
        .from('teams')
        .insert({
          coach_id: coachProfileId,
          name: teamName.trim(),
          sport,
        } as any)
        .select('id, name')
        .single();
      if (error || !team) throw error ?? new Error('Team was not created');

      // Code is generated by the database on insert; read it back through the secure RPC.
      const { data: code, error: codeError } = await supabase
        .rpc('get_team_join_code' as any, { p_team_id: team.id });
      if (codeError || !code) throw codeError ?? new Error('Team code not available');

      setCreatedTeamName(team.name);
      setJoinCode(code as string);

      supabase.functions.invoke('send-coach-welcome-email', {
        body: { team_name: team.name, join_code: code, coach_name: fullName.trim() },
      }).catch(() => { /* non-blocking */ });

      setStep(3);
    } catch (err: any) {
      setTeamError(err?.message || 'Something went wrong creating your team — please try again');

    } finally {
      setLoading(false);
    }
  };

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      toast.success('Copied!');
    } catch {
      toast.error('Copy failed — select the text manually');
    }
  };

  const finish = async () => {
    // Persist completion once so returning coaches never re-enter the wizard.
    try { await supabase.rpc('mark_coach_onboarding_complete' as any); } catch { /* non-blocking */ }
    setStoredViewMode('coach');
    sessionStorage.removeItem(COACH_SIGNUP_ACTIVE_KEY);
    sessionStorage.removeItem('sportara-coach-draft');
    window.location.href = '/';
  };

  const exit = () => {
    sessionStorage.removeItem('sportara-coach-draft');
    sessionStorage.removeItem(COACH_SIGNUP_ACTIVE_KEY);
    onExit();
  };

  if (resuming) {
    return (
      <div className="min-h-screen premium-page-bg flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-coach" />
      </div>
    );
  }


  return (
    <div className="min-h-screen premium-page-bg flex flex-col">
      <div className="px-4 pt-8 pb-4 text-center">
        <Logo className="w-12 h-12 mx-auto" alt="Sportara" />
        <p className="text-[11px] font-display font-bold uppercase tracking-[0.2em] text-coach mt-2">
          Coach Setup
        </p>
      </div>

      <div className="flex-1 flex justify-center px-4 pb-16">
        <div className="w-full max-w-md">
          <AnimatePresence mode="wait">
            {/* ─────────── Screen 1 ─────────── */}
            {step === 1 && (
              <motion.div
                key="s1"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                className="space-y-6 rounded-3xl border border-coach/25 bg-card/60 backdrop-blur-xl p-5 sm:p-6 shadow-lg"
              >
                <StepHeader
                  step={1}
                  onBack={exit}
                  title={accountCreated ? "Coach account created" : "Create your coach account"}
                  subtitle="Just the basics — we'll set up your team next."
                />
                {accountCreated ? <div className="space-y-3"><p className="text-sm text-muted-foreground">Signed in as {email}. Your account is ready; continue with your team.</p><Button className="w-full" onClick={() => setStep(2)}>Continue to team setup</Button></div> : <form onSubmit={handleCreateAccount} className="space-y-3" noValidate>
                  <div className="space-y-1">
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="text"
                        placeholder="Full name"
                        value={fullName}
                        onChange={e => { setFullName(e.target.value); setNameError(''); }}
                        maxLength={80}
                        className={`pl-10 h-12 rounded-xl border-2 font-body ${nameError ? 'border-destructive' : ''}`}
                      />
                    </div>
                    <FieldError message={nameError} />
                  </div>
                  <div className="space-y-1">
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="email"
                        placeholder="Email address"
                        value={email}
                        onChange={e => { setEmail(e.target.value); setEmailError(e.target.value && !EMAIL_RE.test(e.target.value.trim()) ? 'Enter a valid email address' : ''); }}
                        maxLength={255}
                        className={`pl-10 h-12 rounded-xl border-2 font-body ${emailError ? 'border-destructive' : ''}`}
                      />
                    </div>
                    <FieldError message={emailError} />
                  </div>
                  <div className="space-y-1.5">
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Password"
                        value={password}
                        onChange={e => { setPassword(e.target.value); setPasswordError(''); }}
                        className={`pl-10 pr-16 h-12 rounded-xl border-2 font-body ${passwordError ? 'border-destructive' : ''}`}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(v => !v)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-display font-bold text-muted-foreground"
                      >
                        {showPassword ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <p className="text-xs text-muted-foreground">At least 8 characters. Mix letters, numbers and symbols; avoid common passwords.</p>
                    {password && <PasswordStrengthMeter password={password} show />}
                    <FieldError message={passwordError} />
                  </div>
                  <Button
                    type="submit"
                    disabled={loading || !step1Valid}
                    className="w-full h-12 text-base font-display font-bold rounded-xl bg-coach text-coach-foreground hover:bg-coach/90"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create My Coach Account'}
                  </Button>
                </form>}
                {needsEmailConfirm && <p role="status" className="text-sm text-coach">Check {email} for your confirmation link. Return here once confirmed to finish setup.</p>}
              </motion.div>
            )}

            {/* ─────────── Screen 2 ─────────── */}
            {step === 2 && (
              <motion.div
                key="s3"
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -24 }}
                className="space-y-6 rounded-3xl border border-coach/25 bg-card/60 backdrop-blur-xl p-5 sm:p-6 shadow-lg"
              >
                <StepHeader
                  step={2}
                  onBack={() => setStep(1)}
                  title="Set up your team"
                  subtitle="Your roster lives here — you can add more teams later."
                />
                <form onSubmit={handleCreateTeam} className="space-y-4" noValidate>
                  <p className="text-sm font-display font-bold">Team sport</p>
                  <div className="grid grid-cols-2 gap-2">{SPORTS.map(s => <Button type="button" key={s.id} variant={sport === s.id ? 'default' : 'outline'} onClick={() => setSport(s.id)}>{s.emoji} {s.label}</Button>)}</div>
                  <div className="space-y-1.5">
                    <label className="text-sm font-display font-bold">What's your team name?</label>
                    <Input
                      value={teamName}
                      onChange={e => { setTeamName(e.target.value); setTeamError(''); }}
                      placeholder="e.g. Riverside Rapids"
                      maxLength={60}
                      className={`h-12 rounded-xl border-2 font-body ${teamError ? 'border-destructive' : ''}`}
                    />
                  </div>
                  <FieldError message={teamError} />

                  <Button
                    type="submit"
                    disabled={loading || !step3Valid}
                    className="w-full h-12 text-base font-display font-bold rounded-xl bg-coach text-coach-foreground hover:bg-coach/90"
                  >
                    {loading
                      ? <span className="flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Creating your team…</span>
                      : 'Create My Team'}
                  </Button>
                  <p className="text-[11px] text-center text-muted-foreground font-body">
                    Your team code is generated once your team is saved.
                  </p>
                </form>
              </motion.div>
            )}

            {/* ─────────── Screen 3 — only reachable with a real, saved team ─────────── */}
            {step === 3 && joinCode && (
              <motion.div
                key="s4"
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                className="space-y-6 rounded-3xl border border-coach/30 bg-card/60 backdrop-blur-xl p-5 sm:p-6 shadow-lg text-center"
              >
                <StepHeader step={3} onBack={() => setStep(2)} title="Invite players" />
                <motion.div
                  initial={{ scale: 0.6, rotate: -12 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 12 }}
                  className="w-16 h-16 rounded-2xl bg-coach/15 flex items-center justify-center mx-auto"
                >
                  <PartyPopper className="w-8 h-8 text-coach" />
                </motion.div>
                <div>
                  <h2 className="font-display font-bold text-2xl tracking-tight">Your team is ready! 🎉</h2>
                  <p className="text-sm text-muted-foreground font-body mt-1">
                    Share this code with your players' parents to get them signed up.
                  </p>
                </div>

                <button
                  onClick={() => copy(shareMessage)}
                  className="w-full rounded-2xl border-2 border-coach/40 bg-coach/10 py-5 transition-colors hover:bg-coach/15"
                  aria-label="Copy team code"
                >
                  <p className="text-[11px] font-display font-bold uppercase tracking-wider text-coach">
                    {createdTeamName || 'Your team'} — Team Code
                  </p>
                  <p className="font-mono font-bold text-3xl tracking-[0.3em] mt-1">{joinCode}</p>
                  <span className="inline-flex items-center gap-1.5 text-xs font-display font-bold text-coach mt-2">
                    {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                    {copied ? 'Copied' : 'Tap to copy'}
                  </span>
                </button>

                <div className="rounded-2xl bg-muted/60 p-4 text-left">
                  <p className="text-xs font-body text-muted-foreground">{shareMessage}</p>
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <Button variant="outline" className="h-11 rounded-xl font-display font-bold" asChild>
                    <a href={`sms:?&body=${encodeURIComponent(shareMessage)}`}>
                      <MessageSquare className="w-4 h-4 mr-1.5" /> Messages
                    </a>
                  </Button>
                  <Button variant="outline" className="h-11 rounded-xl font-display font-bold" asChild>
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(shareMessage)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <Share2 className="w-4 h-4 mr-1.5" /> WhatsApp
                    </a>
                  </Button>
                  <Button variant="outline" className="h-11 rounded-xl font-display font-bold" asChild>
                    <a
                      href={`mailto:?subject=${encodeURIComponent('Join my team on Sportara')}&body=${encodeURIComponent(shareMessage)}`}
                    >
                      <Send className="w-4 h-4 mr-1.5" /> Email
                    </a>
                  </Button>
                  <Button
                    variant="outline"
                    className="h-11 rounded-xl font-display font-bold"
                    onClick={() => copy(shareMessage)}
                  >
                    <Copy className="w-4 h-4 mr-1.5" /> Copy
                  </Button>
                </div>

                <Button
                  onClick={() => setStep(4)}
                  className="w-full h-12 text-base font-display font-bold rounded-xl bg-coach text-coach-foreground hover:bg-coach/90"
                >
                  Next: Assign your first lesson →
                </Button>
              </motion.div>
            )}
            {step === 4 && joinCode && (
              <motion.div key="s4" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-5 rounded-xl border border-coach/30 bg-card/60 p-5 sm:p-6">
                <StepHeader step={4} onBack={() => setStep(3)} title="Assign your first lesson" subtitle="Your team is ready. Choose a lesson from the dashboard or come back later." />
                <Button onClick={() => { sessionStorage.setItem('sportara-coach-assign-first', '1'); finish(); }} className="w-full bg-coach text-coach-foreground">Assign your first lesson</Button>
                <Button variant="outline" onClick={finish} className="w-full">Go to dashboard for now</Button>
                <p className="text-sm text-muted-foreground">Want a hand setting up? <Link to="/contact" className="text-coach underline">Contact us</Link>.</p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

export default CoachSignupFlow;
